<template>
  <div>
    <div class="page-header">
      <div>
        <h5 class="page-title">My Visitors</h5>
        <p class="page-sub">Your pre-registered and past visitors</p>
      </div>
      <router-link :to="$url('/hr/visitors/pre-register')" class="btn-primary-action">
        <font-awesome-icon icon="plus" /> Pre-Register Visitor
      </router-link>
    </div>

    <!-- Filter -->
    <div class="filter-bar">
      <select v-model="filters.status" class="filter-select" @change="load">
        <option value="">All Statuses</option>
        <option value="pre_registered">Pre-Registered</option>
        <option value="checked_in">Checked In</option>
        <option value="checked_out">Checked Out</option>
        <option value="no_show">No Show</option>
        <option value="cancelled">Cancelled</option>
      </select>
    </div>

    <!-- Skeleton -->
    <div v-if="loading" class="sk-cards">
      <div v-for="n in 6" :key="n" class="sk-card"></div>
    </div>

    <!-- Empty -->
    <div v-else-if="!visits.length" class="empty-state">
      <font-awesome-icon icon="user-clock" class="empty-icon" />
      <p>No upcoming visitors. <router-link :to="$url('/hr/visitors/pre-register')">Pre-register one now.</router-link></p>
    </div>

    <!-- Cards grid -->
    <div v-else class="visits-grid">
      <div v-for="v in visits" :key="v.id" class="visit-card">
        <div class="card-header">
          <div class="vis-avatar vis-avatar--lg">{{ initials(v.visitor) }}</div>
          <div style="flex:1;min-width:0;">
            <div class="vis-name">{{ v.visitor?.first_name }} {{ v.visitor?.last_name }}</div>
            <div class="vis-sub">{{ v.visitor?.company || v.visitor?.email || '—' }}</div>
          </div>
          <span class="chip" :class="statusChip(v.status)">{{ fmtStatus(v.status) }}</span>
        </div>
        <div class="card-details">
          <div class="card-detail-row">
            <font-awesome-icon icon="user-tie" class="detail-icon" />
            <span>{{ hostName(v.host) }}</span>
          </div>
          <div class="card-detail-row">
            <font-awesome-icon icon="clock" class="detail-icon" />
            <span>{{ fmtDatetime(v.scheduled_at) }}</span>
          </div>
          <div class="card-detail-row">
            <font-awesome-icon icon="briefcase" class="detail-icon" />
            <span>{{ v.purpose }}</span>
          </div>
          <div v-if="v.location" class="card-detail-row">
            <font-awesome-icon icon="map-marker-alt" class="detail-icon" />
            <span>{{ v.location }}</span>
          </div>
        </div>
        <div class="card-footer">
          <button class="btn-ghost btn-sm" @click="openDetail(v)">
            <font-awesome-icon icon="eye" /> View
          </button>
          <button v-if="v.qr_token" class="btn-ghost btn-sm" @click="showQr(v)">
            <font-awesome-icon icon="qrcode" /> QR Code
          </button>
          <button v-if="v.status === 'pre_registered'" class="btn-icon btn-icon--red" style="display:inline-flex;align-items:center;gap:5px;padding:5px 10px;font-size:13px;" @click="promptCancel(v)">
            <font-awesome-icon icon="times" /> Cancel
          </button>
        </div>
      </div>
    </div>

    <!-- Pagination -->
    <div v-if="pagination.last_page > 1" style="display:flex;align-items:center;justify-content:center;gap:12px;margin-top:16px;">
      <button class="page-btn" :disabled="pagination.current_page===1" @click="goPage(pagination.current_page-1)">&#8249;</button>
      <span class="page-info">Page {{ pagination.current_page }} of {{ pagination.last_page }}</span>
      <button class="page-btn" :disabled="pagination.current_page===pagination.last_page" @click="goPage(pagination.current_page+1)">&#8250;</button>
    </div>

    <!-- Detail modal -->
    <div v-if="detailVisit" class="modal-overlay" @click.self="detailVisit = null">
      <div class="modal-box">
        <div class="modal-head">
          <h5 class="modal-title">Visitor Details</h5>
          <button class="modal-close" @click="detailVisit = null"><font-awesome-icon icon="times" /></button>
        </div>
        <div class="modal-body" style="gap:0;padding:0;">

          <!-- Identity strip -->
          <div class="vd-identity">
            <div class="vd-identity-left">
              <div class="vd-avatar-lg">{{ initials(detailVisit.visitor) }}</div>
              <div>
                <div class="vd-full-name">{{ detailVisit.visitor?.first_name }} {{ detailVisit.visitor?.last_name }}</div>
                <div v-if="detailVisit.visitor?.company" class="vd-org">{{ detailVisit.visitor.company }}</div>
                <div class="vd-contact-row">
                  <span v-if="detailVisit.visitor?.email" class="vd-contact-item">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
                    {{ detailVisit.visitor.email }}
                  </span>
                  <span v-if="detailVisit.visitor?.phone" class="vd-contact-item">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.13 9.14a19.79 19.79 0 01-3.07-8.67A2 2 0 012.07 .5H5a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.09 8.36a16 16 0 006.29 6.29l1.42-1.42a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/></svg>
                    {{ detailVisit.visitor.phone }}
                  </span>
                </div>
              </div>
            </div>
            <span class="chip vd-status-chip" :class="statusChip(detailVisit.status)">{{ fmtStatus(detailVisit.status) }}</span>
          </div>

          <!-- Visit info -->
          <div class="vd-section">
            <div class="vd-section-title">Visit Information</div>
            <div class="vd-grid">
              <div class="vd-field">
                <div class="vd-label">Host</div>
                <div class="vd-value">{{ hostName(detailVisit.host) }}</div>
              </div>
              <div class="vd-field">
                <div class="vd-label">Purpose</div>
                <div class="vd-value">{{ detailVisit.purpose || '—' }}</div>
              </div>
              <div class="vd-field">
                <div class="vd-label">Scheduled</div>
                <div class="vd-value">{{ fmtDatetime(detailVisit.scheduled_at) }}</div>
              </div>
              <div class="vd-field">
                <div class="vd-label">Location</div>
                <div class="vd-value">{{ detailVisit.location || '—' }}</div>
              </div>
              <div v-if="detailVisit.note" class="vd-field vd-field--full">
                <div class="vd-label">Note</div>
                <div class="vd-value">{{ detailVisit.note }}</div>
              </div>
            </div>
          </div>

        </div>
        <div class="modal-footer">
          <button v-if="detailVisit.qr_token" class="btn-ghost" @click="showQr(detailVisit); detailVisit = null">
            <font-awesome-icon icon="qrcode" /> QR Code
          </button>
          <button v-if="detailVisit.status === 'pre_registered'" class="btn-ghost" style="color:#dc2626;border-color:#fecaca;" @click="promptCancel(detailVisit); detailVisit = null">
            <font-awesome-icon icon="times" /> Cancel Visit
          </button>
          <button class="btn-ghost" @click="detailVisit = null">Close</button>
        </div>
      </div>
    </div>

    <!-- QR modal -->
    <div v-if="qrVisit" class="modal-overlay" @click.self="qrVisit = null">
      <div class="modal-box" style="max-width:360px;">
        <div class="modal-head">
          <h5 class="modal-title">Visitor QR Code</h5>
          <button class="modal-close" @click="qrVisit = null"><font-awesome-icon icon="times" /></button>
        </div>
        <div class="modal-body" style="align-items:center;text-align:center;">
          <p class="text-muted" style="margin:0;">Present at reception for check-in</p>
          <img :src="qrImageUrl(qrVisit.qr_token)" style="width:180px;height:180px;" alt="QR" />
        </div>
        <div class="modal-footer">
          <button class="btn-ghost" @click="qrVisit = null">Close</button>
        </div>
      </div>
    </div>

    <!-- Cancel confirm modal -->
    <div v-if="cancelVisit" class="modal-overlay" @click.self="cancelVisit = null">
      <div class="modal-box" style="max-width:400px;">
        <div class="modal-head">
          <h5 class="modal-title">Cancel Visit</h5>
          <button class="modal-close" @click="cancelVisit = null"><font-awesome-icon icon="times" /></button>
        </div>
        <div class="modal-body">
          <p style="font-size:13px;color:#556;margin:0;">
            Are you sure you want to cancel the visit for
            <strong>{{ cancelVisit.visitor?.first_name }} {{ cancelVisit.visitor?.last_name }}</strong>?
          </p>
          <div class="detail-grid">
            <div class="detail-row">
              <span class="detail-label">Scheduled</span>
              <span class="detail-value">{{ fmtDatetime(cancelVisit.scheduled_at) }}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">Host</span>
              <span class="detail-value">{{ hostName(cancelVisit.host) }}</span>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn-ghost" @click="cancelVisit = null">Keep Visit</button>
          <button class="btn-primary-action" style="background:#dc2626;" @click="confirmCancel">
            Cancel Visit
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import { useToast } from 'vue-toastification'

export default {
  name: 'VisitorUpcoming',
  setup() { return { toast: useToast() } },
  data() {
    return {
      visits: [], loading: true,
      filters: { status: 'pre_registered' },
      pagination: { current_page: 1, last_page: 1 },
      qrVisit: null, cancelVisit: null, detailVisit: null,
    }
  },
  created() { this.load() },
  methods: {
    async load(page = 1) {
      this.loading = true
      try {
        const p = { page, per_page: 12, ...this.filters }
        const r = await axios.get('visitors/me', { params: p })
        const d = r.data.data
        this.visits = d.data ?? d
        this.pagination = { current_page: d.current_page ?? 1, last_page: d.last_page ?? 1 }
      } catch { this.toast.error('Failed to load visitors.') }
      finally { this.loading = false }
    },
    promptCancel(visit) {
      this.cancelVisit = visit
    },
    async confirmCancel() {
      const visit = this.cancelVisit
      try {
        await axios.post(`visitors/me/${visit.id}/cancel`)
        this.toast.success('Visit cancelled.')
        this.cancelVisit = null
        this.load()
      } catch (e) { this.toast.error(e.response?.data?.message ?? 'Failed.') }
    },
    openDetail(visit) { this.detailVisit = visit },
    showQr(visit) { this.qrVisit = visit },
    qrImageUrl(token) {
      return `https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(token)}`
    },
    goPage(p) { this.load(p) },
    initials(v) { return ((v?.first_name?.[0] ?? '') + (v?.last_name?.[0] ?? '')).toUpperCase() },
    hostName(h) { return h ? `${h.first_name} ${h.last_name}` : '—' },
    fmtDatetime(v) {
      if (!v) return '—'
      return new Date(v).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' })
    },
    fmtStatus(s) {
      const map = { pre_registered: 'Pre-Registered', checked_in: 'Checked In',
        checked_out: 'Checked Out', no_show: 'No Show', cancelled: 'Cancelled' }
      return map[s] ?? s
    },
    statusChip(s) {
      return { pre_registered: 'chip--indigo', checked_in: 'chip--green', checked_out: 'chip--purple',
               no_show: 'chip--amber', cancelled: 'chip--red' }[s] ?? 'chip--gray'
    },
  },
}
</script>

<style scoped>
.page-header { display:flex; align-items:flex-start; justify-content:space-between; flex-wrap:wrap; gap:12px; margin-bottom:20px; }
.page-title  { font-size:20px; font-weight:700; color:#1a2b45; margin:0 0 3px; }
.page-sub    { font-size:13px; color:#7a90b0; margin:0; }

.filter-bar { display:flex; flex-wrap:wrap; gap:8px; margin-bottom:14px; }
.filter-select { height:38px; padding:0 12px; border:1px solid #dde5f0; border-radius:8px; font-size:13px; color:#1a2b45; background:#fff; outline:none; font-family:inherit; }
.filter-select:focus { border-color:#004AAD; }

.btn-primary-action { display:inline-flex; align-items:center; gap:6px; background:#004AAD; color:#fff; font-size:13px; font-weight:600; padding:0 18px; height:38px; border-radius:8px; border:none; cursor:pointer; font-family:inherit; text-decoration:none; }
.btn-primary-action:hover:not(:disabled) { background:#003b8e; }
.btn-ghost { display:inline-flex; align-items:center; gap:6px; background:#f4f7fb; color:#3d5068; border:1px solid #e4eaf2; font-size:13px; font-weight:600; padding:0 14px; height:34px; border-radius:8px; cursor:pointer; font-family:inherit; }
.btn-ghost:hover { background:#e4eaf2; }
.btn-sm { height:32px; font-size:12px; }
.btn-icon { background:none; border:none; cursor:pointer; color:#7a90b0; border-radius:6px; }
.btn-icon--red:hover { background:#fef2f2; color:#dc2626; }

.chip { display:inline-flex; align-items:center; padding:3px 10px; border-radius:20px; font-size:11px; font-weight:600; white-space:nowrap; }
.chip--green  { background:#dcfce7; color:#16a34a; }
.chip--gray   { background:#f1f5f9; color:#64748b; }
.chip--indigo { background:#e0e7ff; color:#4338ca; }
.chip--purple { background:#ede9fe; color:#7c3aed; }
.chip--amber  { background:#fef9c3; color:#ca8a04; }
.chip--red    { background:#fce7f3; color:#be185d; }

.visits-grid { display:grid; grid-template-columns:repeat(auto-fill,minmax(300px,1fr)); gap:16px; }
.visit-card  { background:#fff; border:1px solid #e4eaf2; border-radius:12px; padding:16px; display:flex; flex-direction:column; gap:0; }
.card-header { display:flex; align-items:center; gap:10px; margin-bottom:12px; }
.card-details { border-top:1px solid #f0f4f8; padding-top:10px; display:flex; flex-direction:column; gap:5px; }
.card-detail-row { display:flex; align-items:center; gap:8px; font-size:12.5px; color:#3d5068; }
.detail-icon { color:#7a90b0; width:13px; flex-shrink:0; }
.card-footer { display:flex; gap:8px; align-items:center; margin-top:12px; padding-top:10px; border-top:1px solid #f0f4f8; }

.sk-cards { display:grid; grid-template-columns:repeat(auto-fill,minmax(300px,1fr)); gap:16px; }
.sk-card  { height:160px; background:linear-gradient(90deg,#f0f4f8 25%,#e4eaf2 50%,#f0f4f8 75%); background-size:200% 100%; animation:shimmer 1.4s infinite; border-radius:12px; }
@keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }

.modal-overlay { position:fixed; inset:0; background:rgba(0,0,0,.45); z-index:1000; display:flex; align-items:center; justify-content:center; padding:16px; }
.modal-box   { background:#fff; border-radius:14px; width:100%; max-width:520px; max-height:92vh; overflow-y:auto; box-shadow:0 20px 60px rgba(0,0,0,.2); }
.modal-head  { display:flex; align-items:center; justify-content:space-between; padding:18px 22px; border-bottom:1px solid #f0f4f8; }
.modal-title { font-size:15px; font-weight:700; color:#1a2b45; margin:0; }
.modal-close { background:none; border:none; cursor:pointer; color:#7a90b0; font-size:16px; line-height:1; }
.modal-body  { padding:20px 22px; display:flex; flex-direction:column; gap:14px; }
.modal-footer { display:flex; justify-content:flex-end; gap:8px; padding:16px 22px; border-top:1px solid #f0f4f8; }

.detail-grid { background:#f8fafc; border-radius:8px; padding:12px 14px; }
.detail-row  { display:flex; justify-content:space-between; align-items:center; padding:6px 0; border-bottom:1px solid #f0f4f8; font-size:13px; }
.detail-row:last-child { border-bottom:none; }
.detail-label { font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:.04em; color:#aab8cc; }
.detail-value { font-weight:600; color:#1a2b45; font-size:13px; }

.page-btn  { background:#fff; border:1px solid #dde5f0; border-radius:6px; padding:4px 12px; font-size:16px; cursor:pointer; }
.page-btn:disabled { opacity:.4; cursor:not-allowed; }
.page-info { font-size:13px; color:#7a90b0; }

.empty-state { text-align:center; padding:60px 20px; color:#aab8cc; }
.empty-icon  { font-size:3rem; margin-bottom:14px; display:block; color:#dde5f0; }

.vis-avatar { width:32px; height:32px; border-radius:50%; background:#e0e7ff; display:flex; align-items:center; justify-content:center; font-size:12px; font-weight:700; color:#4338ca; flex-shrink:0; }
.vis-avatar--lg { width:40px; height:40px; font-size:14px; }
.vis-name   { font-weight:600; font-size:13px; color:#1a2b45; }
.vis-sub    { font-size:11px; color:#7a90b0; }
.text-muted { color:#7a90b0; font-size:13px; }

/* ── Visitor Detail modal ─────────────────────────────────────────────────── */
.vd-identity { display:flex; align-items:flex-start; justify-content:space-between; gap:12px; padding:20px 22px; border-bottom:1px solid #f0f4f8; }
.vd-identity-left { display:flex; align-items:flex-start; gap:14px; flex:1; min-width:0; }
.vd-avatar-lg { width:52px; height:52px; border-radius:13px; flex-shrink:0; background:linear-gradient(135deg,#004AAD 0%,#1565d8 100%); color:#fff; font-size:18px; font-weight:800; display:flex; align-items:center; justify-content:center; }
.vd-full-name { font-size:16px; font-weight:800; color:#1a2b45; line-height:1.2; margin-bottom:3px; }
.vd-org { font-size:12px; color:#7a90b0; margin-bottom:5px; }
.vd-contact-row { display:flex; flex-wrap:wrap; gap:10px; }
.vd-contact-item { display:inline-flex; align-items:center; gap:5px; font-size:12px; color:#4a5e78; }
.vd-status-chip { flex-shrink:0; align-self:flex-start; }
.vd-section { padding:16px 22px; border-bottom:1px solid #f0f4f8; }
.vd-section-title { font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:.06em; color:#aab8cc; margin-bottom:12px; }
.vd-grid { display:grid; grid-template-columns:1fr 1fr; gap:12px 20px; }
.vd-field { display:flex; flex-direction:column; gap:3px; }
.vd-field--full { grid-column:1 / -1; }
.vd-label { font-size:11px; font-weight:600; text-transform:uppercase; letter-spacing:.05em; color:#aab8cc; }
.vd-value { font-size:13px; font-weight:600; color:#1a2b45; line-height:1.5; }
</style>
